/**
 * infinite-scroll.js  (v2)
 *
 * Progressive-enhancement infinite scroll for the collection product grid.
 *
 * v2 changes — fixes the noticeable pause before new products appear:
 *   1. PREFETCH AHEAD. As soon as one page is appended, the next page is
 *      fetched in the background and held in memory. By the time the customer
 *      reaches the bottom the HTML is usually already parsed, so appending is
 *      effectively instant instead of costing a fresh round-trip.
 *   2. WIDER TRIGGER. Loading starts 1500px before the sentinel is on screen
 *      (was 800px — less than two rows of product cards, so a fast scroll
 *      always outran it).
 *   3. VISIBLE LOADING STATE. If a wait does happen, an explicit "Loading
 *      more…" row appears rather than the grid seeming to freeze. It is
 *      deliberately delayed 120ms so the instant path never flashes it.
 *   4. AUTO-CONTINUE. If a page lands and the sentinel is still on screen
 *      (tall monitor, short page), the next page loads immediately instead of
 *      waiting for another scroll gesture.
 *
 * Unchanged by design:
 *   - The URL bar keeps updating (history.replaceState) so every "page" stays
 *     independently linkable and shareable.
 *   - If JavaScript fails or IntersectionObserver isn't supported, the normal
 *     numbered pagination links underneath still work exactly as before.
 *
 * Scope: only activates inside an element carrying [data-infinite-scroll-root],
 * which is only rendered when the "Enable infinite scroll" section setting is on.
 */
(function () {
  'use strict';

  var LOOKAHEAD_PX = 1500;
  var SPINNER_DELAY_MS = 120;

  function injectKeyframesOnce() {
    if (document.getElementById('infinite-scroll-keyframes')) return;
    var style = document.createElement('style');
    style.id = 'infinite-scroll-keyframes';
    style.textContent =
      '@keyframes infScrollSpin{to{transform:rotate(360deg)}}' +
      '@media (prefers-reduced-motion:reduce){[data-infinite-spin]{animation-duration:2.4s}}';
    document.head.appendChild(style);
  }

  function buildStatusRow() {
    injectKeyframesOnce();
    var el = document.createElement('div');
    el.setAttribute('data-infinite-status', '');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.style.cssText =
      'display:none;justify-content:center;align-items:center;gap:.6em;' +
      'padding:2.5em 0;opacity:.6;font-size:.85em;width:100%;';
    el.innerHTML =
      '<span data-infinite-spin style="display:inline-block;width:1.1em;height:1.1em;' +
      'border:2px solid currentColor;border-right-color:transparent;border-radius:50%;' +
      'animation:infScrollSpin .7s linear infinite;"></span>' +
      '<span>Loading more…</span>';
    return el;
  }

  function initRoot(root) {
    var grid = root.querySelector('[data-grid-large]');
    var paginationWrap = root.querySelector('[data-infinite-pagination]');
    var sentinel = root.querySelector('[data-infinite-sentinel]');

    if (!grid || !paginationWrap || !sentinel ||
        !('IntersectionObserver' in window) || typeof window.fetch !== 'function') {
      return;
    }

    var observer;
    var appending = false;
    var exhausted = false;
    var prefetch = null; // { url: String, promise: Promise<Document|null> }

    var statusRow = buildStatusRow();
    sentinel.parentNode.insertBefore(statusRow, sentinel);

    function showLoading(on) {
      statusRow.style.display = on ? 'flex' : 'none';
    }

    function getNextUrl() {
      var nextLink = paginationWrap.querySelector('[data-pagination-next]');
      return nextLink ? nextLink.getAttribute('href') : null;
    }

    function stop() {
      exhausted = true;
      if (observer) observer.disconnect();
      showLoading(false);
      statusRow.parentNode && statusRow.parentNode.removeChild(statusRow);
      sentinel.setAttribute('hidden', 'hidden');
    }

    /* Warm the next page in the background. Cheap: one GET the browser would
       have made anyway, just earlier. */
    function startPrefetch() {
      if (exhausted) { prefetch = null; return; }
      var url = getNextUrl();
      if (!url) { prefetch = null; return; }
      if (prefetch && prefetch.url === url) return;

      prefetch = {
        url: url,
        promise: window.fetch(url, { credentials: 'same-origin' })
          .then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.text();
          })
          .then(function (html) {
            return new DOMParser().parseFromString(html, 'text/html');
          })
          .catch(function () {
            return null; // fall back to the visible pagination links
          })
      };
    }

    function appendNext() {
      if (appending || exhausted) return;

      var url = getNextUrl();
      if (!url) { stop(); return; }

      if (!prefetch || prefetch.url !== url) startPrefetch();
      if (!prefetch) { stop(); return; }

      appending = true;
      root.setAttribute('data-infinite-loading', 'true');

      var pending = prefetch.promise;
      var settled = false;
      pending.then(function () { settled = true; });
      window.setTimeout(function () {
        if (!settled && appending && !exhausted) showLoading(true);
      }, SPINNER_DELAY_MS);

      pending.then(function (doc) {
        if (!doc) { stop(); return; }

        var newRoot = doc.querySelector('[data-infinite-scroll-root]');
        if (!newRoot) { stop(); return; }

        var newGrid = newRoot.querySelector('[data-grid-large]');
        var newPagination = newRoot.querySelector('[data-infinite-pagination]');

        if (newGrid) {
          var fragment = document.createDocumentFragment();
          while (newGrid.firstChild) {
            fragment.appendChild(newGrid.firstChild);
          }
          grid.appendChild(fragment);
        }

        if (newPagination) {
          paginationWrap.innerHTML = newPagination.innerHTML;
        }

        try {
          history.replaceState(history.state, '', url);
        } catch (err) {
          /* no-op: URL sync is a nice-to-have, never block on it */
        }

        root.dispatchEvent(new CustomEvent('infinite-scroll:appended', { bubbles: true }));
      }).catch(function () {
        /* leave the numbered pagination links intact as a fallback */
      }).then(function () {
        appending = false;
        showLoading(false);
        root.removeAttribute('data-infinite-loading');
        prefetch = null;

        if (exhausted) return;
        if (!getNextUrl()) { stop(); return; }

        startPrefetch();

        /* If the customer is still sitting at the bottom (tall viewport, or a
           page that rendered short), keep going rather than making them nudge
           the scroll wheel again. Uses the real viewport edge, not the
           lookahead margin, so this can't run away on its own. */
        window.requestAnimationFrame(function () {
          if (exhausted || appending) return;
          var rect = sentinel.getBoundingClientRect();
          if (rect.top <= window.innerHeight) appendNext();
        });
      });
    }

    if (!getNextUrl()) return;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) appendNext();
        });
      },
      { rootMargin: LOOKAHEAD_PX + 'px 0px ' + LOOKAHEAD_PX + 'px 0px' }
    );

    observer.observe(sentinel);
    root.setAttribute('data-infinite-enabled', 'true');

    /* Warm page 2 straight away so the very first append is instant too. */
    startPrefetch();
  }

  function init() {
    var roots = document.querySelectorAll('[data-infinite-scroll-root]');
    for (var i = 0; i < roots.length; i++) {
      initRoot(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
